import { makeStyles } from "@material-ui/core/styles";
import SessionContext from "components/session/SessionContext";
import { greybackground, pagePadding } from "data/commonClasses";
import {
  createPassword,
  handleNumber,
  handleRouter,
  setCookie,
} from "functions";
import Link from "next/link";
import { useContext, useState } from "react";
import useTranslation from "translations/hooks/useTranslation";
import Button from "./Button";
import Input from "./Input";
import Text from "./Text";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "auto",
    backgroundImage: `url(${"/engineerzone.png"})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    display: "flex",
    flexDirection: "column",
    margin: "0 auto",
    padding: "2%",
    [theme.breakpoints.down("769")]: {
      width: "100%",
    },
  },
  containerContent: {
    display: "flex",
    flexDirection: "column",
    width: "35%",
    padding: "2%",
    [theme.breakpoints.down("1100")]: {
      width: "70%",
    },
    [theme.breakpoints.down("400")]: {
      width: "95%",
    },
    margin: "0 auto",
  },
  root: {
    "& .MuiInputBase-input": {
      color: "#344450",
    },
    "& label.Mui-focused": {
      color: "#F5B750",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F5B750",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#344450",
      },
      "&:hover fieldset": {
        borderColor: "#344450",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F5B750",
      },
    },
    width: "100%",
    margin: "auto",
    marginBottom: 40,
    [theme.breakpoints.down("769")]: {
      marginBottom: 20,
    },
  },
  rootAR: {
    "& label": {
      transformOrigin: "top left",
      right: 20,
      left: "auto",
      "&.Mui-focused": {
        right: 3,
      },
    },
    "& legend": {
      textAlign: "right",
    },
    "& .MuiInputBase-input": {
      color: "#344450",
    },
    "& label.Mui-focused": {
      color: "#F5B750",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F5B750",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#344450",
      },
      "&:hover fieldset": {
        borderColor: "#344450",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F5B750",
      },
    },
    width: "100%",
    margin: "auto",
    marginBottom: 40,
    [theme.breakpoints.down("769")]: {
      marginBottom: 20,
    },
  },
  multilineColor: {
    padding: "0 2%",
    color: "#344450",
    fontSize: 16,
    [theme.breakpoints.down("1100")]: {
      fontSize: 14,
    },
    [theme.breakpoints.down("769")]: {
      fontSize: 12,
      // height: 25
    },
  },
  button: {
    backgroundColor: "#344450",
    color: "#FFFFFF",
    padding: "6%",
    paddingTop: "3%",
    paddingBottom: "3%",
    margin: "3% 0",
    fontSize: 15,
    "&:hover": {
      backgroundColor: "#F5B750",
    },
    [theme.breakpoints.down("1100")]: {
      fontSize: 12,
    },
    [theme.breakpoints.down("769")]: {
      fontSize: 10,
    },
  },
  linkPage: {
    color: "#344450",
    width: "fit-content",
    fontSize: 15,
    fontWeight: "bold",
    [theme.breakpoints.down("1100")]: {
      fontSize: 14,
    },
    [theme.breakpoints.down("769")]: {
      fontSize: 10,
    },
  },
  linkPageHover: {
    cursor: "pointer",
    fontWeight: "bold",
    "&:hover": {
      color: "#F5B750",
    },
    textDecoration: "underline",
  },
  introSentence: {
    color: "#344450",
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "1%",
    marginTop: "1%",
    [theme.breakpoints.down("769")]: {
      fontSize: 16,
    },
  },
  intro: {
    color: "#344450",
    fontSize: 18,
    textAlign: "center",
    [theme.breakpoints.down("769")]: {
      fontSize: 14,
    },
  },
  errorText: {
    color: "red",
    width: "fit-content",
    fontWeight: "bold",
    margin: "0 auto",
    fontSize: 20,
    [theme.breakpoints.down("1100")]: {
      fontSize: 18,
    },
    [theme.breakpoints.down("769")]: {
      fontSize: 16,
    },
  },
  options: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    [theme.breakpoints.down("280")]: {
      flexDirection: "column",
    },
  },
}));

export default function Signin({ introSentence, intro }) {
  const classes = useStyles();
  const { t } = useTranslation();

  let {
    actions: { signin, setSession },
    session: { lang },
  } = useContext(SessionContext);
  let isAr = lang === "ar";

  const [state, updateState] = useState({
    email: "",
    engineerId:"",
    password: "",
    show: false,

    isWrongEmail: false,
    isWrongPassword: false,
    isWrong: false,
  });

  function setState(nextState) {
    updateState((prevState) => ({
      ...prevState,
      ...nextState,
    }));
  }

  function handleChange(e) {
    let { name, value } = e.target;
    setState({ [name]: value });
    if (state.isWrongEmail) setState({ isWrongEmail: false });
    if (state.isWrongPassword) setState({ isWrongPassword: false });
    if (state.isWrong) setState({ isWrong: false });
  }

  function handleShow() {
    state.show ? setState({ show: false }) : setState({ show: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    let email = state.engineerId;
    let password = createPassword(state.password);
    let response = await signin({ email, password });
    if (response.status == "success") {
      let user = response.response;
      setCookie("_id", user._id, 30);
      setCookie("authToken", user.authToken, 30);
      setSession({ user });
      handleRouter({ href: "/", as: "/" });
    } else {
      if (response.responseType == "EmailDoesNotExists") {
        setState({ isWrongEmail: true });
      } else if (response.responseType == "wrongPassword") {
        setState({ isWrongPassword: true });
      } else {
        setState({ isWrong: true });
      }
    }
  }

  return (
    <div className={`${pagePadding} mb-5`}>
      <div
        className={`${greybackground} flex items-center justify-center flex-col `}
      >
        <Text center bold sectionTitle text={introSentence} />
        <div className="mt-10 flex items-center justify-center w-full md:w-4/5 lg:w-2/3 xl:w-4/12">
          <form
            className="w-full flex flex-col gap-2 "
            onSubmit={handleSubmit}
          >
            <Input
              required
              name="engineerId"
              value={state.engineerId}
              direction={isAr ?"rtl":"ltr"}
              // title={t("engineerId")}
              className={`input w-full`}
              leftIconName={"ic:baseline-engineering"}
              onChange={(e) =>  handleChange({
                  target: {
                    name: e.target.name,
                    value: handleNumber(e.target.value),
                  },
                })
              
              }
              // className={isAr ? classes.rootAR : classes.root}
              // InputProps={{ className: classes.multilineColor }}
              placeholder={t("engineerId")}
            />
            <Input
              required
              name="password"
              direction={isAr ?"rtl":"ltr"}
              isPasswordField
              value={state.password}
              className={`input w-full`}
              onChange={handleChange}
              placeholder={t("password")}
            />

            <div className="flex flex-row justify-end mb-5">
              <Link
                href={`/${lang}/forgotPassword`}
                as={`/${lang}/forgotPassword`}
              >
                <a>
                  <Text
                    className={
                      "text-oeaMain hover:text-orange text-input md:text-infoText font-bold"
                    }
                    text={t("forgotPass")}
                  />
                </a>
              </Link>
            </div>

            <div className={"text-red font-bold text-infoText"}>
              {state.isWrongEmail ? t("userDoesNotExists") : null}
              {state.isWrongPassword ? t("wrongPassword") : null}
              {state.isWrong ? t("wrongData") : null}
            </div>

            <Button
              iconName={`ep:arrow-${lang=='ar'?"left":"right"}-bold`}
              iconClassName="text-sm"
              title={t("signIn")}
              type="submit"
              className="w-full "
            />

            <div className="flex flex-row justify-start">
              <Text
                className={"text-oeaMain text-input md:text-infoText font-bold"}
                text={`${t("newUser")} `}
              />
              <Link href={`/${lang}/enroll`} as={`/${lang}/enroll`}>
                <a>
                  <Text
                    className={
                      "text-oeaMain mx-2 hover:text-orange text-input md:text-infoText font-bold"
                    }
                    text={t("joinToOea")}
                  />
                </a>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
    //     <Box className={classes.intro}>{intro}</Box>
    //     <form onSubmit={handleSubmit}>
    //       <Box className={classes.containerContent}>
    //         <TextField
    //           required
    //           name="email"
    //           value={state.email}
    //           variant="outlined"
    //           label={t("engineerId")}
    //           onChange={(e) =>
    //             handleChange({
    //               target: {
    //                 name: e.target.name,
    //                 value: handleNumber(e.target.value),
    //               },
    //             })
    //           }
    //           className={isAr ? classes.rootAR : classes.root}
    //           InputProps={{ className: classes.multilineColor }}
    //           placeholder={t("engineerId")}
    //         />
    //         <TextField
    //           required
    //           name="password"
    //           type={state.show ? "text" : "password"}
    //           value={state.password}
    //           variant="outlined"
    //           label={t("password")}
    //           onChange={handleChange}
    //           className={isAr ? classes.rootAR : classes.root}
    //           InputProps={{
    //             className: classes.multilineColor,
    //             endAdornment: (
    //               <InputAdornment position="end">
    //                 <IconButton
    //                   type="button"
    //                   edge="end"
    //                   style={{ color: state.show ? "#F5B750" : "#344450" }}
    //                   onClick={handleShow}
    //                 >
    //                   {state.show ? <Visibility /> : <VisibilityOff />}
    //                 </IconButton>
    //               </InputAdornment>
    //             ),
    //           }}
    //           placeholder={t("password")}
    //         />
    //         <Box className={classes.errorText}>
    //           {state.isWrongEmail ? t("userDoesNotExists") : null}
    //           {state.isWrongPassword ? t("wrongPassword") : null}
    //           {state.isWrong ? t("wrongData") : null}
    //         </Box>
    //         <Button type="submit" className={classes.button}>
    //           {t("signIn")}
    //         </Button>
    //         <Box className={classes.options}>
    //           <Box
    //             display={"flex"}
    //             flexDirection={"row"}
    //             justifyContent={"flex-start"}
    //           >
    //             <Box className={classes.linkPage}>{t("newUser")} &nbsp;</Box>
    //             <Link href="/enroll" as="/enroll">
    //               <Box
    //                 className={classes.linkPage + " " + classes.linkPageHover}
    //               >
    //                 {t("joinOea")}
    //               </Box>
    //             </Link>
    //           </Box>
    //           <Box
    //             display={"flex"}
    //             flexDirection={"row"}
    //             justifyContent={"flex-end"}
    //           >
    //             <Link href="/forgotPassword" as="/forgotPassword">
    //               <Box
    //                 className={classes.linkPage + " " + classes.linkPageHover}
    //               >
    //                 {t("forgotPass")}
    //               </Box>
    //             </Link>
    //           </Box>
    //         </Box>
    //       </Box>
    //     </form>
    //   </Box>
    // </Box>
  );
}
